import { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "@/layout/Sidebar";
import TopNavbar from "@/layout/TopNavbar";
import ToggleInput from "@/components/ui/ToggleInput";
import NumberInput from "@/components/ui/NumberInput";
import SectionHeading from "@/components/ui/SectionHeading";
import BrandButton from "@/components/ui/BrandButton";
import Select from "@/components/ui/Select";
import {
  loadSettings,
  setLocalStorageWithExpiry,
  getLocalStorageWithExpiry,
  showApiError,
  clearAuthStorage,
} from "@/utils";
import * as C from "@/const";
import "./page.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Settings() {
  const { t } = useTranslation();
  const [isPermanentlyExpanded, setIsPermanentlyExpanded] = useState(true);
  const [settings, setSettings] = useState(() => loadSettings());
  const [isSavingDone, setIsSavingDone] = useState(true);
  const [assistantMode, setAssistantMode] = useState(() => {
    return (
      getLocalStorageWithExpiry(C.ELELEM_ASSISTANT_MODE_KEY) ||
      C.ASSISTANT_MODE_SPOTLIGHT
    );
  });
  const [showSteps, setShowSteps] = useState(() => {
    const stored = getLocalStorageWithExpiry(C.ELELEM_ASSISTANT_SHOW_STEPS_KEY);
    if (stored === null || stored === undefined) return true;
    return stored === "true" || stored === true;
  });

  const handleAssistantModeChange = (value) => {
    setAssistantMode(value);
  };

  const handleToggleShowSteps = () => {
    setShowSteps((prev) => {
      const next = !prev;
      setLocalStorageWithExpiry(
        C.ELELEM_ASSISTANT_SHOW_STEPS_KEY,
        String(next),
        C.EXPIRY_TIME_IN_MINUTES,
      );
      window.dispatchEvent(
        new CustomEvent("elelem-assistant:show-steps-change", {
          detail: next,
        }),
      );
      return next;
    });
  };

  useEffect(() => {
    const handleModeChange = (e) => {
      if (e.detail) {
        setAssistantMode(e.detail);
      }
    };
    const handleShowStepsChange = (e) => {
      if (typeof e.detail === "boolean") {
        setShowSteps(e.detail);
      }
    };
    window.addEventListener("elelem-assistant:mode-change", handleModeChange);
    window.addEventListener("elelem-assistant:show-steps-change", handleShowStepsChange);
    return () => {
      window.removeEventListener("elelem-assistant:mode-change", handleModeChange);
      window.removeEventListener("elelem-assistant:show-steps-change", handleShowStepsChange);
    };
  }, []);
  const [showSaved, setShowSaved] = useState(false);
  const [isInvalidatingCache, setIsInvalidatingCache] = useState(false);
  const [invalidateCacheMessage, setInvalidateCacheMessage] = useState(null);
  const [isInvalidatingGeoDashboardCache, setIsInvalidatingGeoDashboardCache] =
    useState(false);
  const [
    invalidateGeoDashboardCacheMessage,
    setInvalidateGeoDashboardCacheMessage,
  ] = useState(null);

  const isSuperAdmin = getLocalStorageWithExpiry("role") === "super-admin";

  const location = useLocation();
  const navigate = useNavigate();

  const handleResponse = useCallback(
    (res) => {
      if (res.status === 401) {
        console.warn("Unauthorized access - redirecting to login");
        clearAuthStorage();
        localStorage.setItem(
          "redirectAfterLogin",
          location.pathname + location.search,
        );
        navigate("/login");
        return Promise.reject("Unauthorized");
      }
      if (!res.ok) {
        console.error(`HTTP error: ${res.status} ${res.statusText}`);
        return Promise.reject("Backend error");
      }
      return res.json();
    },
    [location, navigate],
  );

  const toggleExperimentalFeatures = () => {
    const newValue = !settings[C.EXPERIMENTAL_FEATURES_KEY];
    setSettings((prev) => ({
      ...prev,
      [C.EXPERIMENTAL_FEATURES_KEY]: newValue,
    }));
    setLocalStorageWithExpiry(
      C.EXPERIMENTAL_FEATURES_KEY,
      newValue,
      C.EXPIRY_TIME_IN_MINUTES,
    );
  };

  const toggleDoExplicitWebSearch = () => {
    const newValue = !settings.query_research[C.DO_EXPLICIT_WEB_SEARCH_KEY];
    setSettings((prev) => ({
      ...prev,
      query_research: {
        ...prev.query_research,
        [C.DO_EXPLICIT_WEB_SEARCH_KEY]: newValue,
      },
    }));
    setLocalStorageWithExpiry(
      C.DO_EXPLICIT_WEB_SEARCH_KEY,
      newValue,
      C.EXPIRY_TIME_IN_MINUTES,
    );
  };

  const toggleEnableSearchLocalization = () => {
    const newValue = !settings.query_research[C.ENABLE_SEARCH_LOCALIZATION_KEY];
    setSettings((prev) => ({
      ...prev,
      query_research: {
        ...prev.query_research,
        [C.ENABLE_SEARCH_LOCALIZATION_KEY]: newValue,
      },
    }));
    setLocalStorageWithExpiry(
      C.ENABLE_SEARCH_LOCALIZATION_KEY,
      newValue,
      C.EXPIRY_TIME_IN_MINUTES,
    );
  };

  const updateShareOfCitationsThreshold = (value) => {
    setSettings((prev) => ({
      ...prev,
      optimize_content: {
        ...prev.optimize_content,
        [C.SHARE_OF_CITATIONS_THRESHOLD_KEY]: value,
      },
    }));
    setLocalStorageWithExpiry(
      C.SHARE_OF_CITATIONS_THRESHOLD_KEY,
      value,
      C.EXPIRY_TIME_IN_MINUTES,
    );
  };

  const updateUrlCitationShareThreshold = (value) => {
    setSettings((prev) => ({
      ...prev,
      optimize_content: {
        ...prev.optimize_content,
        [C.URL_CITATION_SHARE_THRESHOLD_KEY]: value,
      },
    }));
    setLocalStorageWithExpiry(
      C.URL_CITATION_SHARE_THRESHOLD_KEY,
      value,
      C.EXPIRY_TIME_IN_MINUTES,
    );
  };

  const updateDistanceThreshold = (value) => {
    setSettings((prev) => ({
      ...prev,
      optimize_content: {
        ...prev.optimize_content,
        [C.DISTANCE_THRESHOLD_KEY]: value,
      },
    }));
    setLocalStorageWithExpiry(
      C.DISTANCE_THRESHOLD_KEY,
      value,
      C.EXPIRY_TIME_IN_MINUTES,
    );
  };

  const invalidateCache = () => {
    setIsInvalidatingCache(true);
    setInvalidateCacheMessage(null);
    fetch(`${BACKEND_URL}/api/v1/optimize_content/invalidate_cache`, {
      method: "PUT",
      credentials: "include",
    })
      .then(handleResponse)
      .then((data) => {
        setInvalidateCacheMessage({ type: "success", text: data.message });
        setTimeout(() => setInvalidateCacheMessage(null), 3000);
      })
      .catch((err) => {
        console.error("Error invalidating cache:", err);
        setInvalidateCacheMessage({
          type: "error",
          text: "Failed to invalidate cache.",
        });
        setTimeout(() => setInvalidateCacheMessage(null), 3000);
      })
      .finally(() => {
        setIsInvalidatingCache(false);
      });
  };

  const invalidateGeoDashboardCache = () => {
    setIsInvalidatingGeoDashboardCache(true);
    setInvalidateGeoDashboardCacheMessage(null);
    fetch(`${BACKEND_URL}/api/v1/geo-dashboard/cache`, {
      method: "DELETE",
      credentials: "include",
    })
      .then(handleResponse)
      .then((data) => {
        setInvalidateGeoDashboardCacheMessage({
          type: "success",
          text: data.message,
        });
        setTimeout(() => setInvalidateGeoDashboardCacheMessage(null), 3000);
      })
      .catch((err) => {
        console.error("Error invalidating GEO Dashboard cache:", err);
        setInvalidateGeoDashboardCacheMessage({
          type: "error",
          text: "Failed to invalidate GEO Dashboard cache.",
        });
        setTimeout(() => setInvalidateGeoDashboardCacheMessage(null), 3000);
      })
      .finally(() => {
        setIsInvalidatingGeoDashboardCache(false);
      });
  };

  const saveSettings = () => {
    setIsSavingDone(false);
    setLocalStorageWithExpiry(
      C.ELELEM_ASSISTANT_MODE_KEY,
      assistantMode,
      C.EXPIRY_TIME_IN_MINUTES,
    );
    window.dispatchEvent(
      new CustomEvent("elelem-assistant:mode-change", { detail: assistantMode }),
    );
    setLocalStorageWithExpiry(
      C.ELELEM_ASSISTANT_SHOW_STEPS_KEY,
      String(showSteps),
      C.EXPIRY_TIME_IN_MINUTES,
    );
    window.dispatchEvent(
      new CustomEvent("elelem-assistant:show-steps-change", { detail: showSteps }),
    );
    fetch(`${BACKEND_URL}/api/v1/settings`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(settings),
    })
      .then(handleResponse)
      .then(() => {
        setShowSaved(true);
        setTimeout(() => setShowSaved(false), 1000);
      })
      .catch((err) => {
        console.error("Error saving settings:", err);
        showApiError(
          t("settings.saveSettingsError", "Failed to save settings. Please try again."),
        );
      })
      .finally(() => {
        setIsSavingDone(true);
      });
  };

  const toggleSidebar = () => {
    setIsPermanentlyExpanded(!isPermanentlyExpanded);
  };

  return (
    <div className="app-container">
      <Sidebar
        currentPage={"Not Found"}
        isPermanentlyExpanded={isPermanentlyExpanded}
      />
      <main className="content bg-page">
        <div className="container-fluid p-4">
          <TopNavbar
            isPermanentlyExpanded={isPermanentlyExpanded}
            toggleSidebar={toggleSidebar}
          />
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
            <div>
              <h3 className="h4 fw-bold text-dark mb-0">
                {t("settings.title", "Settings")}
              </h3>
              <p className="mt-1 mb-0 fs-6 text-secondary">
                {t("settings.subtitle", "Manage your account settings here.")}
              </p>
            </div>
            {Object.keys(settings).length > 0 && (
              <BrandButton
                style={{ minHeight: "38px" }}
                disabled={!isSavingDone}
                onClick={saveSettings}
              >
                {showSaved ? (
                  t("common.saved", "Saved!")
                ) : isSavingDone ? (
                  t("settings.saveSettings", "Save Settings")
                ) : (
                  <div
                    className="spinner-border"
                    role="status"
                    style={{ width: "1.2rem", height: "1.2rem" }}
                  >
                    <span className="visually-hidden">
                      {t("common.saving", "Saving...")}
                    </span>
                  </div>
                )}
              </BrandButton>
            )}
          </div>
          <br />
          {Object.keys(settings).length > 0 && (
            <>
              <div className="row g-5">
                <div className="col-12 col-md-6" style={{ maxWidth: 400 }}>
                  <SectionHeading className="mb-2">
                    {t("settings.features", "Features")}
                  </SectionHeading>
                  <div className="mb-1">
                    <ToggleInput
                      label={t(
                        "settings.enableExperimental",
                        "Enable Experimental Features",
                      )}
                      checked={settings[C.EXPERIMENTAL_FEATURES_KEY]}
                      onChange={toggleExperimentalFeatures}
                    />
                  </div>
                  <br />
                  <SectionHeading className="mb-2">
                    {t("settings.assistantMode", "Elelem Assistant Mode")}
                  </SectionHeading>
                  <div className="mb-2" style={{ maxWidth: 300 }}>
                    <Select
                      options={[
                        {
                          value: C.ASSISTANT_MODE_BOTTOM_RIGHT,
                          label: t("settings.modeSidebar", "Bottom-Right Mode"),
                        },
                        {
                          value: C.ASSISTANT_MODE_SPOTLIGHT,
                          label: t("settings.modeSpotlight", "Spotlight Mode"),
                        },
                      ]}
                      value={assistantMode}
                      onChange={(e) => handleAssistantModeChange(e.target.value)}
                      className="form-select border-secondary text-dark"
                      dataTestId="assistant-mode-select"
                    />
                  </div>
                  <div className="mb-1">
                    <ToggleInput
                      label={t("settings.showAiSteps", "Show AI steps")}
                      checked={showSteps}
                      onChange={handleToggleShowSteps}
                    />
                  </div>
                  <br />
                  <SectionHeading className="mb-2">
                    {t("settings.promptResearchSection", "Prompt Research")}
                  </SectionHeading>
                  <div className="mb-1">
                    <ToggleInput
                      label={t(
                        "settings.doExplicitWebSearch",
                        "Do Explicit Web Search",
                      )}
                      checked={
                        settings.query_research[C.DO_EXPLICIT_WEB_SEARCH_KEY]
                      }
                      onChange={toggleDoExplicitWebSearch}
                    />
                  </div>
                  <div className="mb-1">
                    <ToggleInput
                      label={t(
                        "settings.enableSearchLocalization",
                        "Enable Search Localization",
                      )}
                      checked={
                        settings.query_research[
                          C.ENABLE_SEARCH_LOCALIZATION_KEY
                        ]
                      }
                      onChange={toggleEnableSearchLocalization}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-6" style={{ maxWidth: 400 }}>
                  <SectionHeading className="mb-2">
                    {t("settings.optimizeContentSection", "Optimize Content")}
                  </SectionHeading>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                    }}
                  >
                    <NumberInput
                      label={t(
                        "settings.genaiPromptsSocThreshold",
                        "GenAI Prompts - Share of Citations threshold",
                      )}
                      defaultValue={
                        settings.optimize_content[
                          C.SHARE_OF_CITATIONS_THRESHOLD_KEY
                        ]
                      }
                      onChange={updateShareOfCitationsThreshold}
                    />
                    <NumberInput
                      label={t(
                        "settings.closestUrlsShareThreshold",
                        "Pages that best answer this prompt - URL citation share threshold",
                      )}
                      defaultValue={
                        settings.optimize_content[
                          C.URL_CITATION_SHARE_THRESHOLD_KEY
                        ]
                      }
                      onChange={updateUrlCitationShareThreshold}
                    />
                    <NumberInput
                      label={t(
                        "settings.relatedPromptsDistanceThreshold",
                        "Related prompts - distance threshold",
                      )}
                      defaultValue={
                        settings.optimize_content[C.DISTANCE_THRESHOLD_KEY]
                      }
                      onChange={updateDistanceThreshold}
                      min={0.0}
                      step={0.1}
                      max={1.0}
                    />
                  </div>
                </div>
              </div>
              <br />
               {isSuperAdmin && (
                <>
                  <SectionHeading className="mb-2">
                    {t("settings.cacheManagement", "Cache Management")}
                  </SectionHeading>
                  <p className="fs-6 text-secondary mb-2">
                    {t(
                      "settings.invalidateOptimizeContentDesc",
                      "Invalidate all Optimize Content cache entries (FastAPI cache and content brief cache).",
                    )}
                  </p>
                  <button
                    className="btn fw-medium mb-1"
                    style={{
                      backgroundColor: "#6c757d",
                      color: "#fff",
                      border: "none",
                      minHeight: "38px",
                    }}
                    disabled={isInvalidatingCache}
                    onClick={invalidateCache}
                  >
                    {isInvalidatingCache ? (
                      <div
                        className="spinner-border"
                        role="status"
                        style={{
                          color: "#fff",
                          width: "1.2rem",
                          height: "1.2rem",
                        }}
                      >
                        <span className="visually-hidden">
                          {t("settings.invalidating", "Invalidating...")}
                        </span>
                      </div>
                    ) : (
                      t(
                        "settings.invalidateOptimizeContentCache",
                        "Invalidate Optimize Content Cache",
                      )
                    )}
                  </button>
                  {invalidateCacheMessage && (
                    <div
                      className={`alert mt-2 py-1 px-2 ${
                        invalidateCacheMessage.type === "success"
                          ? "alert-success"
                          : "alert-danger"
                      }`}
                    >
                      {invalidateCacheMessage.text}
                    </div>
                  )}
                  <br />
                  <p className="fs-6 text-secondary mb-2">
                    {t(
                      "settings.invalidateGeoDashboardDesc",
                      "Invalidate all GEO Dashboard cache entries.",
                    )}
                  </p>
                  <button
                    className="btn fw-medium mb-1"
                    style={{
                      backgroundColor: "#6c757d",
                      color: "#fff",
                      border: "none",
                      minHeight: "38px",
                    }}
                    disabled={isInvalidatingGeoDashboardCache}
                    onClick={invalidateGeoDashboardCache}
                  >
                    {isInvalidatingGeoDashboardCache ? (
                      <div
                        className="spinner-border"
                        role="status"
                        style={{
                          color: "#fff",
                          width: "1.2rem",
                          height: "1.2rem",
                        }}
                      >
                        <span className="visually-hidden">
                          {t("settings.invalidating", "Invalidating...")}
                        </span>
                      </div>
                    ) : (
                      t(
                        "settings.invalidateGeoDashboardCache",
                        "Invalidate GEO Dashboard Cache",
                      )
                    )}
                  </button>
                  {invalidateGeoDashboardCacheMessage && (
                    <div
                      className={`alert mt-2 py-1 px-2 ${
                        invalidateGeoDashboardCacheMessage.type === "success"
                          ? "alert-success"
                          : "alert-danger"
                      }`}
                    >
                      {invalidateGeoDashboardCacheMessage.text}
                    </div>
                  )}
                  <br />
                </>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default Settings;
